"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import Button from "@/shared/ui/components/button";
import LoadingCheckout from "@/shared/ui/components/loading-checkout";
import Badge from "@/shared/ui/components/badge";
import { BrandLogoIcon } from "@/shared/ui/icons/brand-logo-icon";
import { CartModal } from "@/shared/ui/modals/cart-modal";
import { useTranslation } from "react-i18next";
import { setCheckoutLocaleCookie } from "@/services/cookie";
import {
  type AppContext,
  CheckoutStatus,
  CHECKOUT_STATUS_PROCESSING_PAYMENT,
  Currency,
  Page,
  SessionError,
  type CheckoutSession,
  type AppEvent,
} from "@/types";
import { useCosmosClient } from "@/services/cosmos";
import { CksCheckoutStateSchema } from "@/services/cosmos/schema/checkout-service";
import { createLocalizedIcon } from "@/shared/ui/icons/localized-icon";
import { useMoneyAmount } from "@/shared/ui/components/money-amount";
import { DEFAULT_CHECKOUT_SESSION_CONFIGURATION } from "@/lib/session-config";
import { navigateToPage } from "@/lib/routes";
import { withCheckoutId } from "@/lib/checkout-id";
import { emptySession } from "@/lib/checkout";
import * as mobileLib from "@/lib/mobile";
import * as lib3ds from "@/lib/3ds";
import { newLocalStorageStore } from "@/lib/store";
import { useCheckoutSessionV3, getRouteForSession } from "@/modules/checkout-session";
import { ErrorScreen } from "@/shared/ui/layout/error-screen";
import { logError, logInfo } from "@/services/logging/logger";
import * as events from "@/events";
import { usePageUnloadTracking } from "@/shared/hooks/use-page-unload-tracking";

export interface AppWrapperProps {
  children: (ctx: AppContext) => React.ReactNode;
  stickyFooter?: (ctx: AppContext) => React.ReactNode;
  stickyFooterWithShadow?: boolean; // adds light box-shadow to the top of sticky footer when true
  visible?: boolean; // controls fade on the gray background container
  showLanguageSwitcher?: boolean; // toggles language switcher visibility
  headerBottomSpacing?: "default" | "none"; // controls margin below header (gap to content)
  headerLeft?: React.ReactNode | ((ctx: AppContext) => React.ReactNode); // full override for left side of header
  showLogo?: boolean; // toggles logo visibility when using default header left
  showCartButton?: boolean; // toggles cart button visibility
  bare?: boolean; // hides the header when true
  withOverlay?: (ctx: AppContext) => boolean; // optional function that determines if overlay should be shown
  disableHeaderInteractions?: boolean; // disables header buttons when true (e.g., during processing)
  onSessionFetch?: (ctx: AppContext) => void;
}

const displayDate = (dateString: string, locale: string = "en-US"): string => {
  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original string if invalid
    }

    // Normalize locale format (convert en_US to en-US)
    const normalizedLocale = locale.replace("_", "-");

    // Use Intl.DateTimeFormat for locale-aware formatting
    // while ensuring Gregorian calendar regardless of language.
    return new Intl.DateTimeFormat(`${normalizedLocale}-u-ca-gregory`, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  } catch {
    return dateString; // Return original string if formatting fails
  }
};

export default function AppWrapper({
  children,
  stickyFooter,
  stickyFooterWithShadow = false,
  visible = true,
  showLanguageSwitcher = true,
  headerBottomSpacing = "default",
  headerLeft,
  showLogo = true,
  showCartButton = true,
  bare = false,
  withOverlay,
  disableHeaderInteractions = false,
  onSessionFetch,
}: AppWrapperProps) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const cosmosClient = useCosmosClient();
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  // Static config — /checkout/configuration is no longer called (CXO-2337).
  const configuration = DEFAULT_CHECKOUT_SESSION_CONFIGURATION;

  const shouldSkipSessionFetch = useMemo(() => {
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const result = searchParams.get("result");
    const is3dsFailure = pathname.includes("3ds") && result === "failed";
    const isPaymentMethodFailure =
      pathname.includes("instalment/add-payment-method") &&
      pathname.includes("failed");
    const shouldSkip = is3dsFailure || isPaymentMethodFailure;

    return shouldSkip;
  }, [location.pathname, location.search]);

  const { data: session, isLoading } = useCheckoutSessionV3({
    skip: shouldSkipSessionFetch,
  });

  const isArabic = i18n.language.startsWith("ar");
  const langLabel = isArabic ? "English" : "العربية";
  const store = useMemo(
    () => newLocalStorageStore(window, session?.id ?? "unknown"),
    [session?.id],
  );

  // Track page unload events
  usePageUnloadTracking(session);

  useEffect(() => {
    (async () => {
      if (!session) {
        return;
      }
      const store = newLocalStorageStore(window, session.id);
      try {
        await lib3ds.handleRedirection(window, store);
        await lib3ds.logThreedsReturnFromBankIfNeeded(
          session,
          store,
          window.location.pathname,
        );
      } catch (err) {
        logError(
          "app-wrapper",
          "Error handling 3DS redirection",
          err instanceof Error ? err : new Error(String(err)),
          {
            error: err,
            sessionId: session?.id,
            pathname: window.location.pathname,
          },
        );
      }

      // Don't navigate away from /3ds page - it handles its own navigation
      // This prevents loops where AppWrapper tries to navigate to /confirmation
      // while /3ds is showing the countdown before redirecting to the bank
      if (
        location.pathname === "/3ds" ||
        location.pathname === "/questionnaire" ||
        location.pathname === "/questionnaire/return" ||
        location.pathname === "/kyc" ||
        location.pathname === "/kyc/return"
      ) {
        return;
      }

      // /early-repayment self-manages while the BE is in the processing-payment
      // window after the user submits the early-repayment payment. Without this
      // skip, AppWrapper would auto-redirect to /confirmation as soon as the
      // status flipped to PROCESSING_*.
      if (
        location.pathname === "/early-repayment" &&
        CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(session.status)
      ) {
        return;
      }

      const correctRoute = getRouteForSession(session);

      if (correctRoute === "" || correctRoute === location.pathname) {
        return;
      }

      navigate(withCheckoutId(correctRoute), { replace: true });
    })();
  }, [session, navigate, location.pathname]);

  // Get the currency from the session, defaulting to SAR
  const sessionCurrency = session?.order?.currency ?? Currency.SAR;
  const moneyAmount = useMoneyAmount(sessionCurrency, i18n.language);

  const amount = useCallback(
    (amount: number | string) => {
      return moneyAmount(amount, sessionCurrency);
    },
    [moneyAmount, sessionCurrency],
  );

  const getCheckoutSession = useCallback(async () => {
    const sessionData = CksCheckoutStateSchema.parse(
      await cosmosClient.getCheckoutSessionV3(),
    );

    return sessionData;
  }, [cosmosClient]);

  const navigateToPageCallback = useCallback(
    (page: Page) => navigateToPage(navigate, page),
    [navigate],
  );

  const displayDateCallback = useCallback(
    (date: string) => displayDate(date, i18n.language),
    [i18n.language],
  );

  const deviceSupports = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => (window as any)?.ApplePaySession !== undefined,
    [],
  );

  const selectedPayment = useCallback(() => {
    const paymentOption = (session?.paymentOptions ?? []).find(
      (paymentOption) =>
        paymentOption.offerId === session?.selectedPaymentOptionId,
    );

    return paymentOption
      ? {
          amountToPayNow: paymentOption.downPaymentAmount.amount,
          paymentOption,
        }
      : { amountToPayNow: 0, paymentOption: null };
  }, [session?.paymentOptions, session?.selectedPaymentOptionId]);

  const sendEvent = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: AppEvent, data?: Record<string, any>) => {
      const attributes = {
        ...(data ?? {}),
        checkout_id: session?.id ?? "",
        order_id: session?.order?.id ?? "",
      };

      window.dispatchEvent(
        new CustomEvent("app_event", {
          detail: {
            name: e.name,
            attributes,
          },
        }),
      );

      logInfo("event", e.name, attributes);
    },
    [session?.id, session?.order?.id],
  );

  const sendMobileMessage = useCallback(
    (source: string, action: string) =>
      mobileLib.sendMessage(session?.order?.id ?? "", source, action),
    [session?.order?.id],
  );

  const switchLanguage = useCallback(async () => {
    const currentIsArabic = i18n.language.startsWith("ar");
    const targetLang = currentIsArabic ? "en_US" : "ar_SA";

    sendEvent(events.SWITCH_LANGUAGE_CLICKED, {
      switch_to: targetLang,
    });

    setCheckoutLocaleCookie(targetLang);

    const url = new URL(window.location.href);
    url.searchParams.set("locale", targetLang);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );

    await i18n.changeLanguage(targetLang);
  }, [sendEvent, i18n]);

  const ctx: AppContext = useMemo(
    () => ({
      rtl: isArabic,
      localizedIcon: createLocalizedIcon(isArabic),
      amount,
      navigate: navigateToPageCallback,
      getCheckoutSession,
      session: session ?? emptySession,
      t,
      displayDate: displayDateCallback,
      store,
      configuration,
      applePay: {
        deviceSupports,
      },
      selectedPayment,
      sendEvent,
      sendMobileMessage,
    }),
    [
      isArabic,
      amount,
      navigateToPageCallback,
      getCheckoutSession,
      session,
      t,
      displayDateCallback,
      store,
      configuration,
      deviceSupports,
      selectedPayment,
      sendEvent,
      sendMobileMessage,
    ],
  );

  useEffect(() => {
    if (session?.id && onSessionFetch) {
      onSessionFetch(ctx);
    }
  }, [session?.id]);

  const sessionError = session?.error?.type ?? SessionError.NONE;
  const sessionHasError = sessionError !== SessionError.NONE;

  // Determine if overlay should be shown
  const shouldShowOverlay = withOverlay ? withOverlay(ctx) : false;

  // Prevent body scrolling when overlay is active
  useEffect(() => {
    if (shouldShowOverlay) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [shouldShowOverlay]);

  return (
    <div
      className={`min-h-screen bg-gray-100 flex flex-col transition-opacity duration-500 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="w-full md:w-[480px] mx-auto bg-white min-h-screen md:rounded-t-[32px] md:min-h-[calc(100vh-24px)] md:mt-[24px] md:h-[calc(100vh-24px) md:shadow-xs flex flex-col">
        {/* Scrollable content area */}
        <div
          className={`flex-1 ${shouldShowOverlay ? "overflow-hidden" : "overflow-y-auto"} ${stickyFooter ? "pb-[140px]" : ""}`}
        >
          <div
            className={`${bare ? "pb-2" : "pt-4 pb-2"} flex flex-col flex-grow relative`}
          >
            {/* Header - only show when not bare */}
            {!bare && (
              <div
                className={[
                  "flex items-center border-b px-6 pb-4 border-[#EBEEF4] justify-between",
                  headerBottomSpacing === "none" ? "mb-0" : "mb-5",
                ].join(" ")}
              >
                <div
                  className={`flex items-center gap-4 transition-opacity ${disableHeaderInteractions ? "opacity-50" : ""}`}
                >
                  {headerLeft && !sessionHasError ? (
                    typeof headerLeft === "function" ? (
                      headerLeft(ctx)
                    ) : (
                      headerLeft
                    )
                  ) : (
                    <>{showLogo && <BrandLogoIcon />}</>
                  )}
                </div>
                {!sessionHasError &&
                  (showCartButton || showLanguageSwitcher) &&
                  (!isLoading || !!session?.id) && (
                    <div className="flex items-center gap-4">
                      {showLanguageSwitcher && (
                        <Button
                          variant="plain"
                          size="small"
                          className="w-auto px-0"
                          onClick={switchLanguage}
                          style={{
                            color: "var(--Content-Mid, #585B7A)",
                            textAlign: "center",
                            fontFamily: "IBM Plex Sans",
                            fontSize: "14px",
                            fontStyle: "normal",
                            fontWeight: "400",
                            lineHeight: "18px",
                          }}
                        >
                          {langLabel}
                        </Button>
                      )}
                      {showCartButton && (
                        <div
                          onClick={() => {
                            if (disableHeaderInteractions) return;
                            if (!ctx.session.order?.items?.length) return;
                            ctx.sendEvent(events.HEADER_CART_CLICKED);
                            setIsCartModalOpen(true);
                          }}
                          className={`transition-colors ${disableHeaderInteractions ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:text-gray-800"}`}
                        >
                          <Badge
                            variant="neutral"
                            radius="medium"
                            leftContent={
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4.57039 14.4103C4.23405 14.4103 3.95005 14.2943 3.71839 14.0622C3.48683 13.8302 3.37105 13.546 3.37105 13.2097C3.37105 12.8733 3.48705 12.5893 3.71905 12.3577C3.95105 12.1261 4.23522 12.0103 4.57155 12.0103C4.908 12.0103 5.192 12.1263 5.42355 12.3583C5.65522 12.5903 5.77105 12.8745 5.77105 13.2108C5.77105 13.5472 5.65505 13.8312 5.42305 14.0628C5.19094 14.2945 4.90672 14.4103 4.57039 14.4103ZM11.2269 14.4103C10.8904 14.4103 10.6064 14.2943 10.3749 14.0622C10.1432 13.8302 10.0274 13.546 10.0274 13.2097C10.0274 12.8733 10.1434 12.5893 10.3754 12.3577C10.6075 12.1261 10.8917 12.0103 11.2281 12.0103C11.5644 12.0103 11.8484 12.1263 12.0801 12.3583C12.3116 12.5903 12.4274 12.8745 12.4274 13.2108C12.4274 13.5472 12.3114 13.8312 12.0794 14.0628C11.8474 14.2945 11.5632 14.4103 11.2269 14.4103ZM3.32739 2.53335H12.9069C13.1912 2.53335 13.4051 2.65151 13.5484 2.88785C13.6917 3.12418 13.6924 3.36757 13.5506 3.61801L11.3121 7.65635C11.2044 7.85212 11.0542 8.01007 10.8616 8.13018C10.669 8.25029 10.4576 8.31035 10.2274 8.31035H5.26589L4.65555 9.39751C4.58722 9.50007 4.58511 9.61118 4.64922 9.73085C4.71333 9.85051 4.8095 9.91035 4.93772 9.91035H12.4607V11.0103H4.57105C4.09327 11.0103 3.74477 10.8076 3.52555 10.402C3.30633 9.99646 3.30439 9.59496 3.51972 9.19751L4.45555 7.47701L2.00439 2.30001H0.699219V1.20001H2.69405L3.32739 2.53335Z"
                                  fill="#16181D"
                                />
                              </svg>
                            }
                            className="h-[40px] px-4 py-2 text-sm"
                          >
                            <span className="font-semibold text-[#16181D]">
                              {amount(ctx.session.orderTotalAmount.amount)}
                            </span>
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            )}

            <div className="flex-grow flex flex-col">
              {appWrapperBody(
                ctx,
                sessionError,
                children,
                isLoading,
                session,
                location.pathname,
              )}
            </div>

            {/* Overlay - covers header and content but not sticky footer */}
            {shouldShowOverlay && (
              <div className="absolute inset-0 bg-white/60 pointer-events-auto z-20" />
            )}
          </div>
        </div>

        {/* Optional sticky footer area at the bottom of the card with gradient fade */}
        {!sessionHasError && stickyFooter && (
          <div
            className={`fixed bottom-0 left-1/2 transform -translate-x-1/2 w-full md:w-[480px] bg-white border-t border-[#EBEEF4] z-[70] ${stickyFooterWithShadow ? "shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]" : ""}`}
          >
            <div className="relative p-6">
              {/* Upward fading gradient to emphasize stickiness */}
              <div className="pointer-events-none absolute top-0 left-0 right-0 h-6 bg-gradient-to-t from-transparent to-transparent" />
              {stickyFooter(ctx)}
            </div>
          </div>
        )}

        {/* Cart modal attached to this layout (used when no onCartClick override is provided) */}
        {
          <CartModal
            isOpen={isCartModalOpen}
            onClose={() => setIsCartModalOpen(false)}
            ctx={ctx}
          />
        }
      </div>
    </div>
  );
}

const appWrapperBody = (
  ctx: AppContext,
  error: SessionError,
  children: (ctx: AppContext) => React.ReactNode,
  loading: boolean,
  session: CheckoutSession | undefined,
  pathname: string,
) => {
  const isFailureStatus =
    session?.status === CheckoutStatus.FAILED ||
    session?.status === CheckoutStatus.INVALIDATED;
  // Hard rule (Abanoub / BE, CXO-2657): credit_decline_info wins over
  // error_details — see resolveSessionErrorFromCheckoutPayload.
  const hasCreditDeclineUi = Boolean(session?.creditDeclineInfo);
  if (
    (error !== SessionError.NONE || isFailureStatus) &&
    !hasCreditDeclineUi
  ) {
    return <ErrorScreen ctx={ctx} />;
  }

  const isConfirmationRoute =
    pathname === "/confirmation" ||
    pathname.startsWith("/instalment/add-payment-method/") ||
    pathname.startsWith("/3ds");

  const shouldShowLoading = loading && !session?.id && !isConfirmationRoute;

  if (shouldShowLoading) return <LoadingCheckout ctx={ctx} />;

  return children(ctx);
};
